import { formatNum, formatPercent, formatUsd } from "@bridgesplit/utils";
import { AxisValueFormatterContext } from "@mui/x-charts/internals";

import { FormatType } from "./types";

export const SERIES_FORMATTERS: Record<FormatType, (value: number | null) => string> = {
    [FormatType.Percent]: (value) => percentFormatter(value, { digits: 2, decimals: 1 }),
    [FormatType.Date]: dateFormatter,
    [FormatType.Usd]: usdFormatter,
    [FormatType.Token]: tokenFormatter,
    [FormatType.Hidden]: hiddenFormatter,
    [FormatType.Datetime]: (v: number | null) => dateFormatter(v, { includeTime: true })
};

export const AXIS_FORMATTERS: Record<
    FormatType,
    ((value: number | null, context: AxisValueFormatterContext) => string) | ((value: number | null) => string)
> = {
    [FormatType.Percent]: contextualPercentFormatter,
    [FormatType.Date]: contextualDateFormatter,
    [FormatType.Usd]: contextualUsdFormatter,
    [FormatType.Token]: contextualTokenFormatter,
    [FormatType.Hidden]: hiddenFormatter,
    [FormatType.Datetime]: contextualDatetimeFormatter
};

// Wrapped formatters to handle nulls

function hiddenFormatter(_: number | null) {
    return "";
}

function tokenFormatter(value: number | null, options?: { compactThreshold?: number; decimals?: number }) {
    if (value === null) return "";
    return formatNum(value, {
        longThreshold: options?.compactThreshold,
        customDecimals: options?.decimals
    });
}

function percentFormatter(value: number | null, options?: { digits?: number; decimals?: number }) {
    if (value === null) return "";
    return formatPercent(value, {
        skipMultiplication: false,
        customDigits: options?.digits,
        customDecimals: options?.decimals
    });
}

function dateFormatter(value: number | null, options?: { includeTime?: boolean; includeMinutes?: boolean }) {
    if (value === null) return "";
    const date = new Date(value * 1000);

    if (!options?.includeTime) return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const minutes = date.getMinutes();
    date.setMinutes(minutes < 15 ? 0 : minutes < 45 ? 30 : 60, 0, 0);
    return date
        .toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: options?.includeMinutes ? "numeric" : undefined,
            hour12: true
        })
        .replace(/\sAM/, "AM")
        .replace(/\sPM/, "PM");
}

function usdFormatter(
    value: number | null,
    options?: {
        compactThreshold?: number;
        formatZero?: boolean;
        minimumSignificantDigits?: number;
    }
) {
    if (value === null) return "";
    if (value === 0 && options?.formatZero) return "$0";
    return formatUsd(value, {
        compactThreshold: options?.compactThreshold,
        minimumSignificantDigits: options?.minimumSignificantDigits
    });
}

function contextualDateFormatter(value: number | null, context: AxisValueFormatterContext) {
    return context.location === "tick"
        ? dateFormatter(value)
        : dateFormatter(value, { includeTime: true, includeMinutes: true });
}
function contextualDatetimeFormatter(value: number | null, context: AxisValueFormatterContext) {
    return context.location === "tick"
        ? dateFormatter(value, { includeTime: true, includeMinutes: false })
        : dateFormatter(value, { includeTime: true, includeMinutes: true });
}

function contextualPercentFormatter(value: number | null, context: AxisValueFormatterContext) {
    return context.location === "tick"
        ? percentFormatter(value, { decimals: 0 })
        : percentFormatter(value, { digits: 2, decimals: 1 });
}

function contextualUsdFormatter(value: number | null, context: AxisValueFormatterContext) {
    return context.location === "tick"
        ? usdFormatter(value, { compactThreshold: 1, minimumSignificantDigits: 1, formatZero: true })
        : usdFormatter(value);
}

function contextualTokenFormatter(value: number | null, context: AxisValueFormatterContext) {
    return context.location === "tick" ? tokenFormatter(value, { compactThreshold: 1 }) : tokenFormatter(value);
}
